/**
 * ExchangeContactsAdapter - Exchange/Microsoft 365 Contacts API adapter
 * 
 * CHANGE HISTORY:
 * - 251030_0128 [pending] danielbeer: Moved to Interactr from Synkr (better architecture)
 * - 251030_0057 [pending] danielbeer: Exchange Contacts adapter using Microsoft Graph API
 * 
 * Uses:
 * - Interactr for HTTP requests
 * - Authr for OAuth2 authentication
 * - Microsoft Graph API v1.0
 */

import { BaseApiAdapter } from './BaseApiAdapter.js';
import type { ContactChange } from './types.js';

export interface ExchangeContactsConfig {
  interactr: any; // InteractrManager instance
  clientId: string;
  clientSecret: string;
  tenantId?: string; // Optional, defaults to 'common'
  refreshToken?: string;
  keystoreRef?: {
    provider: string;
    path: string;
    key: string;
  };
}

export class ExchangeContactsAdapter extends BaseApiAdapter {
  private config: ExchangeContactsConfig;
  private interactr: any;
  private graphUrl = 'https://graph.microsoft.com/v1.0';
  private requestId: string | null = null;

  constructor(config: ExchangeContactsConfig) {
    super('exchange-contacts');
    this.config = config;
    this.interactr = config.interactr;
  }

  /**
   * Initialize adapter - create Interactr request
   */
  async initialize(): Promise<void> {
    if (!this.interactr) {
      throw new Error('Interactr not configured');
    }

    const tenantId = this.config.tenantId || 'common';

    // Create API request in Interactr
    const request = await this.interactr.createRequest({
      name: 'Exchange Contacts - List',
      method: 'GET',
      url: `${this.graphUrl}/me/contacts`,
      queryParams: {
        $select: 'id,displayName,givenName,surname,emailAddresses,mobilePhone,businessPhones,homePhones,businessAddress,homeAddress,companyName,jobTitle,birthday,photo',
        $top: '999',
      },
      auth: {
        type: 'oauth2',
        config: {
          grantType: 'authorization_code',
          authUrl: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`,
          tokenUrl: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
          clientId: this.config.clientId,
          clientSecret: this.config.clientSecret,
          scope: 'https://graph.microsoft.com/Contacts.Read https://graph.microsoft.com/Contacts.ReadWrite offline_access',
          refreshToken: this.config.refreshToken,
        },
        keystoreRef: this.config.keystoreRef,
      },
      tags: ['exchange', 'microsoft', 'contacts', 'sync'],
      createdBy: 'synkr',
      updatedBy: 'synkr',
    });

    this.requestId = request.id;
  }

  /**
   * Pull changes from Exchange Contacts
   */
  async pull(lastSync?: number): Promise<ContactChange[]> {
    if (!this.requestId) {
      await this.initialize();
    }

    try {
      // Execute request via Interactr (handles OAuth2 automatically)
      const response = await this.interactr.executeRequest(this.requestId);

      if (response.status !== 200) {
        throw new Error(`Microsoft Graph API error: ${response.status} ${response.statusText}`);
      }

      const data = response.body;
      const contacts = data.value || [];

      // Convert Exchange contacts to ContactChange format
      const changes: ContactChange[] = contacts.map((contact) => {
        // Extract contact data
        const contactData = {
          id: contact.id,
          displayName: contact.displayName || '',
          firstName: contact.givenName || '',
          lastName: contact.surname || '',
          emails: (contact.emailAddresses || []).map((e, idx: number) => ({
            id: `email-${idx}`,
            email: e.address || '',
            type: e.name?.toLowerCase() || 'other',
            isPrimary: idx === 0,
          })),
          phones: [
            ...(contact.mobilePhone ? [{
              id: 'phone-mobile',
              number: contact.mobilePhone,
              type: 'mobile',
              isPrimary: true,
            }] : []),
            ...(contact.businessPhones || []).map((p: string, idx: number) => ({
              id: `phone-work-${idx}`,
              number: p,
              type: 'work',
              isPrimary: false,
            })),
            ...(contact.homePhones || []).map((p: string, idx: number) => ({
              id: `phone-home-${idx}`,
              number: p,
              type: 'home',
              isPrimary: false,
            })),
          ],
          addresses: [
            ...(contact.businessAddress ? [{
              id: 'address-work',
              street: contact.businessAddress.street || '',
              city: contact.businessAddress.city || '',
              state: contact.businessAddress.state || '',
              zip: contact.businessAddress.postalCode || '',
              country: contact.businessAddress.countryOrRegion || '',
              type: 'work',
              isPrimary: true,
            }] : []),
            ...(contact.homeAddress ? [{
              id: 'address-home',
              street: contact.homeAddress.street || '',
              city: contact.homeAddress.city || '',
              state: contact.homeAddress.state || '',
              zip: contact.homeAddress.postalCode || '',
              country: contact.homeAddress.countryOrRegion || '',
              type: 'home',
              isPrimary: false,
            }] : []),
          ],
          company: contact.companyName || '',
          jobTitle: contact.jobTitle || '',
          birthday: contact.birthday || '',
          photo: contact.photo ? `${this.graphUrl}/me/contacts/${contact.id}/photo/$value` : '',
          source: 'exchange',
          syncSource: 'exchange',
        };

        // Get last modified time
        const lastModified = contact.lastModifiedDateTime;
        const timestamp = lastModified ? new Date(lastModified).getTime() : Date.now();

        // Only include if modified after lastSync
        if (lastSync && timestamp <= lastSync) {
          return null;
        }

        return {
          id: contact.id,
          type: 'update' as const,
          data: contactData,
          timestamp,
          source: 'exchange-contacts',
        };
      }).filter((c) => c !== null);

      // Handle pagination if there are more contacts
      if (data['@odata.nextLink']) {
        // TODO: Implement pagination
        console.log('More contacts available, pagination not yet implemented');
      }

      return changes;
    } catch (error) {
      throw new Error(`Failed to pull from Exchange Contacts: ${error.message}`);
    }
  }

  /**
   * Push change to Exchange Contacts
   */
  async push(change: ContactChange): Promise<boolean> {
    if (!this.interactr) {
      throw new Error('Interactr not configured');
    }

    try {
      const tenantId = this.config.tenantId || 'common';
      const method = change.type === 'create' ? 'POST' : 'PATCH';
      const url = change.type === 'create' 
        ? `${this.graphUrl}/me/contacts`
        : `${this.graphUrl}/me/contacts/${change.id}`;

      // Convert contact to Microsoft Graph format
      const graphContact = this.toGraphFormat(change.data);

      const request = await this.interactr.createRequest({
        name: `Exchange Contacts - ${change.type === 'create' ? 'Create' : 'Update'}`,
        method,
        url,
        body: {
          type: 'json',
          content: graphContact,
        },
        auth: {
          type: 'oauth2',
          config: {
            grantType: 'authorization_code',
            authUrl: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`,
            tokenUrl: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
            clientId: this.config.clientId,
            clientSecret: this.config.clientSecret,
            scope: 'https://graph.microsoft.com/Contacts.ReadWrite offline_access',
            refreshToken: this.config.refreshToken,
          },
          keystoreRef: this.config.keystoreRef,
        },
        tags: ['exchange', 'microsoft', 'contacts', 'sync'],
        createdBy: 'synkr',
        updatedBy: 'synkr',
      });

      const response = await this.interactr.executeRequest(request.id);

      return response.status === 200 || response.status === 201;
    } catch (error) {
      console.error(`Failed to push to Exchange Contacts: ${error.message}`);
      return false;
    }
  }

  /**
   * Convert OpCentral contact to Microsoft Graph format
   */
  private toGraphFormat(contact): any {
    const graphContact: any = {
      displayName: contact.displayName || '',
      givenName: contact.firstName || '',
      surname: contact.lastName || '',
    };

    // Add emails
    if (contact.emails && contact.emails.length > 0) {
      graphContact.emailAddresses = contact.emails.map((e) => ({
        address: e.email,
        name: e.type || 'other',
      }));
    }

    // Add phones
    if (contact.phones && contact.phones.length > 0) {
      const mobilePhone = contact.phones.find((p) => p.type === 'mobile');
      const workPhones = contact.phones.filter((p) => p.type === 'work');
      const homePhones = contact.phones.filter((p) => p.type === 'home');

      if (mobilePhone) {
        graphContact.mobilePhone = mobilePhone.number;
      }
      if (workPhones.length > 0) {
        graphContact.businessPhones = workPhones.map((p) => p.number);
      }
      if (homePhones.length > 0) {
        graphContact.homePhones = homePhones.map((p) => p.number);
      }
    }

    // Add addresses
    if (contact.addresses && contact.addresses.length > 0) {
      const workAddress = contact.addresses.find((a) => a.type === 'work');
      const homeAddress = contact.addresses.find((a) => a.type === 'home');

      if (workAddress) {
        graphContact.businessAddress = {
          street: workAddress.street || '',
          city: workAddress.city || '',
          state: workAddress.state || '',
          postalCode: workAddress.zip || '',
          countryOrRegion: workAddress.country || '',
        };
      }
      if (homeAddress) {
        graphContact.homeAddress = {
          street: homeAddress.street || '',
          city: homeAddress.city || '',
          state: homeAddress.state || '',
          postalCode: homeAddress.zip || '',
          countryOrRegion: homeAddress.country || '',
        };
      }
    }

    // Add company and job title
    if (contact.company) {
      graphContact.companyName = contact.company;
    }
    if (contact.jobTitle) {
      graphContact.jobTitle = contact.jobTitle;
    }

    // Add birthday
    if (contact.birthday) {
      graphContact.birthday = contact.birthday;
    }

    return graphContact;
  }

  /**
   * Get adapter name
   */

  /**
   * Check if adapter is available
   */
  async isAvailable(): Promise<boolean> {
    return !!this.interactr;
  }
}
